import { forwardRef, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}

export function Field({ label, hint, error, required, className, children }: FieldProps) {
  return (
    <div className={className}>
      {label && (
        <label className="label-base">
          {label}
          {required && <span className="ml-0.5 text-negative">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="mt-1 text-xs text-negative">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-ink-subtle dark:text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  invalid?: boolean;
  /** Rendered inside the field, before the input (e.g. a unit or symbol). */
  leading?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, leading, ...props },
  ref,
) {
  if (leading) {
    return (
      <div
        className={cn(
          'input-base flex items-center gap-2 p-0 focus-within:ring-2 focus-within:ring-brand-500',
          invalid && 'border-negative',
          className,
        )}
      >
        <span className="pl-3 text-sm text-ink-subtle dark:text-slate-500">{leading}</span>
        <input
          ref={ref}
          className="w-full bg-transparent py-2 pr-3 text-sm outline-none placeholder:text-ink-subtle"
          {...props}
        />
      </div>
    );
  }

  return (
    <input ref={ref} className={cn('input-base', invalid && 'border-negative', className)} {...props} />
  );
});

/**
 * Money input. Keeps the value as a STRING all the way to the API — binding an
 * amount to a JS number is how paise go missing.
 */
export const MoneyInput = forwardRef<HTMLInputElement, InputProps>(function MoneyInput(
  { className, invalid, ...props },
  ref,
) {
  return (
    <div
      className={cn(
        'input-base flex items-center gap-1 p-0 focus-within:ring-2 focus-within:ring-brand-500',
        invalid && 'border-negative',
        className,
      )}
    >
      <span className="pl-3 text-sm text-ink-subtle dark:text-slate-500">₹</span>
      <input
        ref={ref}
        type="text"
        inputMode="decimal"
        // Digits, one decimal point, at most two decimals.
        pattern="^\d*\.?\d{0,2}$"
        className="tabular w-full bg-transparent py-2 pr-3 text-right text-sm outline-none"
        {...props}
      />
    </div>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return <textarea ref={ref} className={cn('input-base min-h-[72px] resize-y', className)} {...props} />;
  },
);

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, invalid, children, ...props },
  ref,
) {
  return (
    <select
      ref={ref}
      className={cn('input-base cursor-pointer pr-8', invalid && 'border-negative', className)}
      {...props}
    >
      {children}
    </select>
  );
});
